import React from 'react';
import SlaBadge from './SlaBadge';

// A board card. It used to carry title, tags and a price, which meant triaging the board
// was opening every card to discover why it was stuck. It now shows the four things that
// answer that from the outside: what is blocking it, what has been built, where the class
// will be filed, and who is sitting on it.
const matchClass = (m) =>
  m === 'strong' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
  : m === 'partial' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
  : m === 'no' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

// Orange is this palette's warning colour and blue its information colour, so a stopped
// build (someone must act) reads orange and a healthy in-progress state reads blue.
const BLOCKER_TONE = {
  build_stopped: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  sources: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-200',
  class_target: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-200',
  price_override: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-200',
  review_overdue: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
};

const buildLabel = (p) => {
  if (!p) return null;
  if (p.stalled) return 'build stopped';
  if (p.status === 'generating') return 'writing…';
  const n = p.artifact_count || 0;
  return `${n} artifact${n === 1 ? '' : 's'} · ${String(p.status).replace(/_/g, ' ')}`;
};

const Chip = ({ tone, title, children }) => (
  <span className={`text-[9.5px] px-2 py-0.5 rounded ${tone}`} title={title}>{children}</span>
);

export default function ConceptCard({ concept: c, onOpen }) {
  const build = buildLabel(c.production);
  const target = c.class_target;

  return (
    <button
      onClick={() => onOpen(c.id)}
      className="block w-full text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 mb-2 hover:shadow-md transition"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] text-gray-400">{c.id}</div>
        {c.sla_due_at && <SlaBadge dueAt={c.sla_due_at} breachedAt={c.sla_breached_at} />}
      </div>

      <div className="text-sm font-semibold leading-snug my-1">{c.title}</div>

      {/* The blocker is the reason to open this card at all, so it sits above the
          descriptive tags rather than among them. One chip only: a card listing four
          problems is as unreadable as one listing none. */}
      {c.blocker && (
        <div className="mb-1.5">
          <Chip tone={BLOCKER_TONE[c.blocker.code] || BLOCKER_TONE.sources}>
            ⚠ {c.blocker.label}
          </Chip>
        </div>
      )}

      <div className="flex gap-1.5 flex-wrap mb-1">
        {c.program && <Chip tone="bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">{c.program}</Chip>}
        {c.learning_level && <Chip tone="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">{c.learning_level}</Chip>}
        {c.delivery && <Chip tone="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">{c.delivery}</Chip>}
        {c.existing_zu_match_result && (
          <Chip tone={matchClass(c.existing_zu_match_result)}>{c.existing_zu_match_result}</Chip>
        )}
      </div>

      {/* Where the class will be filed. A revision points content at an existing class
          folder, so getting it wrong matters - which is why it belongs on the board and
          not only in the inspector. */}
      {target?.disposition && (
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 truncate">
          {target.disposition === 'revision' ? '↻ revises' : '✦ new class'}
          {target.name ? ` ${target.name}` : ''}
          {!target.confirmed && <span className="text-orange-600 dark:text-orange-400"> · unconfirmed</span>}
        </div>
      )}

      {build && (
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 truncate">🛠 {build}</div>
      )}

      {c.client_name && <div className="text-[10px] text-gray-500 mb-1 truncate">Client: {c.client_name}</div>}
      {c.owner && <div className="text-[10px] text-gray-500 mb-1 truncate">With: {c.owner}</div>}

      <div className="flex justify-between text-[11px] text-gray-500">
        <span>{String(c.request_source || '').replace(/_/g, ' ')}</span>
        {c.approved_price != null ? (
          <span className="font-bold text-green-600" title="Director-approved price">
            ${Number(c.approved_price).toLocaleString()}
          </span>
        ) : c.weighted_value != null ? (
          <span className="text-gray-400" title="Pipeline forecast (not the price)">
            ~${Number(c.weighted_value).toLocaleString()} est.
          </span>
        ) : null}
      </div>
    </button>
  );
}
